import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.ensureDefaultOptionSelected();
    this.updateOptionVisibility();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Updates visibility of options (e.g. hides Size option and Custom Options app block when Unstitched is selected).
   */
  updateOptionVisibility() {
    const checkedRadios = Array.from(
      this.querySelectorAll('input[type="radio"]:checked, input[type="radio"][data-current-checked="true"]')
    );
    const selects = Array.from(this.querySelectorAll('select.variant-option__select'));

    let isUnstitched = false;

    checkedRadios.forEach((radio) => {
      if (radio instanceof HTMLInputElement) {
        const val = (radio.value || '').trim().toLowerCase();
        if (val.includes('unstitch')) {
          isUnstitched = true;
        }
      }
    });

    selects.forEach((select) => {
      if (select instanceof HTMLSelectElement) {
        const val = (select.value || '').trim().toLowerCase();
        if (val.includes('unstitch')) {
          isUnstitched = true;
        }
      }
    });

    if (document.body) {
      if (isUnstitched) {
        document.body.classList.add('vari-size--unstitched');
      } else {
        document.body.classList.remove('vari-size--unstitched');
      }
    }

    const sizeElements = this.querySelectorAll(
      '[data-option-name="Size"], [data-option-name="size"], [data-option-name="SIZE"], [data-option-name*="size" i]'
    );
    sizeElements.forEach((container) => {
      if (container instanceof HTMLElement) {
        if (isUnstitched) {
          container.style.setProperty('display', 'none', 'important');
        } else {
          container.style.removeProperty('display');
        }

        const labels = container.querySelectorAll('.variant-option__button-label');
        let firstFabricInput = null;
        let isFabricChecked = false;
        let firstValidSizeInput = null;
        let isValidSizeChecked = false;

        labels.forEach((label) => {
          if (label instanceof HTMLElement) {
            const input = label.querySelector('input[type="radio"]');
            const val = (input && input instanceof HTMLInputElement ? input.value : label.textContent || '').trim().toLowerCase();
            const isFabric = val.includes('fabric');

            if (isUnstitched) {
              if (isFabric && input && input instanceof HTMLInputElement) {
                if (!firstFabricInput) firstFabricInput = input;
                if (input.checked || input.dataset.currentChecked === 'true') {
                  isFabricChecked = true;
                }
              }
            } else {
              if (isFabric) {
                label.style.setProperty('display', 'none', 'important');
              } else {
                label.style.removeProperty('display');
                if (input && input instanceof HTMLInputElement) {
                  if (!firstValidSizeInput) firstValidSizeInput = input;
                  if (input.checked || input.dataset.currentChecked === 'true') {
                    isValidSizeChecked = true;
                  }
                }
              }
            }
          }
        });

        if (isUnstitched && firstFabricInput && !isFabricChecked) {
          firstFabricInput.checked = true;
          firstFabricInput.dataset.currentChecked = 'true';
          firstFabricInput.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (!isUnstitched && firstValidSizeInput && !isValidSizeChecked) {
          firstValidSizeInput.checked = true;
          firstValidSizeInput.dataset.currentChecked = 'true';
          firstValidSizeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const selects = container.querySelectorAll('select.variant-option__select');
        selects.forEach((select) => {
          if (select instanceof HTMLSelectElement) {
            const options = Array.from(select.options);
            let firstFabricOpt = null;
            let isFabricSelected = false;
            let firstValidOpt = null;
            let isValidOptSelected = false;

            options.forEach((opt) => {
              const val = (opt.value || opt.textContent || '').trim().toLowerCase();
              const isFabric = val.includes('fabric');

              if (isUnstitched) {
                if (isFabric) {
                  if (!firstFabricOpt) firstFabricOpt = opt;
                  if (opt.selected) isFabricSelected = true;
                }
              } else {
                if (isFabric) {
                  opt.disabled = true;
                  opt.hidden = true;
                  opt.style.setProperty('display', 'none', 'important');
                } else {
                  opt.disabled = false;
                  opt.hidden = false;
                  opt.style.removeProperty('display');
                  if (!firstValidOpt) firstValidOpt = opt;
                  if (opt.selected) isValidOptSelected = true;
                }
              }
            });

            if (isUnstitched && firstFabricOpt && !isFabricSelected) {
              select.value = firstFabricOpt.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            } else if (!isUnstitched && firstValidOpt && !isValidOptSelected) {
              select.value = firstValidOpt.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });

        const sizeGuides = container.querySelectorAll('.vari-size-guide-inline');
        sizeGuides.forEach((sg) => {
          if (sg instanceof HTMLElement) {
            if (isUnstitched) {
              sg.style.setProperty('display', 'none', 'important');
            } else {
              sg.style.removeProperty('display');
            }
          }
        });
      }
    });

    const selectors = [
      '[class*="custom_options"]',
      '[class*="custom-options"]',
      '[id*="custom_options"]',
      '[id*="custom-options"]',
      '[class*="multi_varients"]',
      '[class*="multi-varients"]',
      '[id*="multi_varients"]',
      '[id*="multi-varients"]',
      '[data-block-type*="custom_options"]',
      '[data-block-type*="custom-options"]',
      '[data-block-type*="multi_varients"]',
      '[data-block-type*="multi-varients"]',
      '[class*="fast_product_options"]',
      '[class*="fast-product-options"]'
    ];

    const appElements = document.querySelectorAll(selectors.join(','));
    appElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        if (isUnstitched) {
          el.style.setProperty('display', 'none', 'important');
        } else {
          el.style.removeProperty('display');
        }
      }
    });

    const shopifyAppBlocks = document.querySelectorAll('.shopify-app-block');
    shopifyAppBlocks.forEach((el) => {
      if (el instanceof HTMLElement) {
        const htmlStr = (el.outerHTML || '').toLowerCase();
        if (
          htmlStr.includes('custom_option') ||
          htmlStr.includes('custom-option') ||
          htmlStr.includes('custom option') ||
          htmlStr.includes('multi_varient') ||
          htmlStr.includes('multi-varient') ||
          htmlStr.includes('multivarient') ||
          htmlStr.includes('fast_product_option') ||
          htmlStr.includes('fast-product-option')
        ) {
          if (isUnstitched) {
            el.style.setProperty('display', 'none', 'important');
          } else {
            el.style.removeProperty('display');
          }
        }
      }
    });
  }

  /**
   * Ensures that every option fieldset has at least one checked option.
   */
  ensureDefaultOptionSelected() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input[type="radio"]') ?? []);
      if (radios.length === 0) return;

      const hasChecked = radios.some((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      if (!hasChecked) {
        const defaultRadio = radios.find((r) => r.dataset.optionAvailable !== 'false') || radios[0];
        if (defaultRadio) {
          defaultRadio.checked = true;
          defaultRadio.dataset.currentChecked = 'true';
          defaultRadio.dataset.previousChecked = 'false';
          this.updateFieldsetCss(fieldsetIndex);
        }
      }
    });

    const selects = Array.from(this.querySelectorAll('select.variant-option__select'));
    selects.forEach((select) => {
      if (select instanceof HTMLSelectElement) {
        const hasSelected = Array.from(select.options).some((opt) => opt.hasAttribute('selected') || opt.selected);
        if (!hasSelected && select.options.length > 0) {
          select.selectedIndex = 0;
          select.options[0].setAttribute('selected', 'selected');
        }
      }
    });

    this.updateOptionVisibility();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.updateOptionVisibility();
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      morphElementSelector,
      selectedOption.dataset.optionValueId
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Builds the fallback request URL with only the specified option value ID.
   * @param {string} optionValueId
   * @returns {string}
   */
  buildRequestUrlForOptionValue(optionValueId) {
    let productUrl = this.#pendingRequestUrl || this.dataset.productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${optionValueId}`);

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [primaryOptionValueId] - The option value ID selected by the user.
   * @param {boolean} [isRetry] - Whether this fetch is a fallback retry.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, primaryOptionValueId, isRetry = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        const parsedVariant = textContent ? JSON.parse(textContent) : null;

        // If the combination returned null/invalid variant, retry immediately with just the clicked option value
        if (!parsedVariant && !isRetry && primaryOptionValueId) {
          const fallbackUrl = this.buildRequestUrlForOptionValue(primaryOptionValueId);
          this.fetchUpdatedSection(fallbackUrl, morphElementSelector, primaryOptionValueId, true);
          return;
        }

        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        this.ensureDefaultOptionSelected();

        // Update URL on product page if a valid variant was resolved
        const isOnProductPage =
          this.dataset.templateProductMatch === 'true' &&
          !this.closest('product-card') &&
          !this.closest('quick-add-dialog');

        if (isOnProductPage && parsedVariant?.id) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', parsedVariant.id);
          if (url.href !== window.location.href) {
            yieldToMainThread().then(() => {
              history.replaceState({}, '', url.toString());
            });
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(parsedVariant, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#radios = [];
    this.#checkedIndices = [];
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.ensureDefaultOptionSelected();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}


// import { Component } from '@theme/component';
// import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
// import { morph, MORPH_OPTIONS } from '@theme/morph';
// import { OverflowList } from '@theme/overflow-list';
// import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

// /**
//  * @typedef {object} VariantPickerRefs
//  * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
//  * @property {HTMLElement} [overflowList] - The overflow list element.
//  */

// /**
//  * A custom element that manages a variant picker.
//  *
//  * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
//  * @extends Component<TRefs>
//  */
// export default class VariantPicker extends Component {
//   /** @type {string | undefined} */
//   #pendingRequestUrl;

//   /** @type {AbortController | undefined} */
//   #abortController;

//   /** @type {number[][]} */
//   #checkedIndices = [];

//   /** @type {HTMLInputElement[][]} */
//   #radios = [];

//   #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

//   connectedCallback() {
//     super.connectedCallback();
//     this.ensureDefaultOptionSelected();
//     this.updateOptionVisibility();
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

//     fieldsets.forEach((fieldset) => {
//       const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
//       this.#radios.push(radios);

//       const initialCheckedIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
//       if (initialCheckedIndex !== -1) {
//         this.#checkedIndices.push([initialCheckedIndex]);
//       } else {
//         this.#checkedIndices.push([]);
//       }
//     });

//     this.addEventListener('change', this.variantChanged.bind(this));
//     this.#resizeObserver.observe(this);
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     this.#resizeObserver.disconnect();
//   }

//   /**
//    * Updates visibility of options (e.g. hides Size option when Unstitched is selected).
//    */
//   updateOptionVisibility() {
//     const checkedRadios = Array.from(
//       this.querySelectorAll('input[type="radio"]:checked, input[type="radio"][data-current-checked="true"]')
//     );
//     const selects = Array.from(this.querySelectorAll('select.variant-option__select'));

//     let isUnstitched = false;

//     checkedRadios.forEach((radio) => {
//       if (radio instanceof HTMLInputElement) {
//         const optName = (radio.dataset.optionName || '').trim().toLowerCase();
//         const val = (radio.value || '').trim().toLowerCase();
//         if ((optName.includes('blouse') || optName.includes('type')) && val.includes('unstitch')) {
//           isUnstitched = true;
//         }
//       }
//     });

//     selects.forEach((select) => {
//       if (select instanceof HTMLSelectElement) {
//         const val = (select.value || '').trim().toLowerCase();
//         if (val.includes('unstitch')) {
//           isUnstitched = true;
//         }
//       }
//     });

//     const sizeElements = this.querySelectorAll(
//       '[data-option-name="Size"], [data-option-name="size"], [data-option-name="SIZE"]'
//     );
//     sizeElements.forEach((el) => {
//       if (el instanceof HTMLElement) {
//         if (isUnstitched) {
//           el.style.setProperty('display', 'none', 'important');
//         } else {
//           el.style.removeProperty('display');
//         }
//       }
//     });
//   }

//   /**
//    * Ensures that every option fieldset has at least one checked option.
//    */
//   ensureDefaultOptionSelected() {
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

//     fieldsets.forEach((fieldset, fieldsetIndex) => {
//       const radios = Array.from(fieldset?.querySelectorAll('input[type="radio"]') ?? []);
//       if (radios.length === 0) return;

//       const hasChecked = radios.some((radio) => radio.checked || radio.dataset.currentChecked === 'true');
//       if (!hasChecked) {
//         const defaultRadio = radios.find((r) => r.dataset.optionAvailable !== 'false') || radios[0];
//         if (defaultRadio) {
//           defaultRadio.checked = true;
//           defaultRadio.dataset.currentChecked = 'true';
//           defaultRadio.dataset.previousChecked = 'false';
//           this.updateFieldsetCss(fieldsetIndex);
//         }
//       }
//     });

//     const selects = Array.from(this.querySelectorAll('select.variant-option__select'));
//     selects.forEach((select) => {
//       if (select instanceof HTMLSelectElement) {
//         const hasSelected = Array.from(select.options).some((opt) => opt.hasAttribute('selected') || opt.selected);
//         if (!hasSelected && select.options.length > 0) {
//           select.selectedIndex = 0;
//           select.options[0].setAttribute('selected', 'selected');
//         }
//       }
//     });

//     this.updateOptionVisibility();
//   }

//   /**
//    * Handles the variant change event.
//    * @param {Event} event - The variant change event.
//    */
//   variantChanged(event) {
//     if (!(event.target instanceof HTMLElement)) return;

//     const selectedOption =
//       event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

//     if (!selectedOption) return;

//     this.updateSelectedOption(event.target);
//     this.updateOptionVisibility();
//     this.dispatchEvent(new VariantSelectedEvent({
//       id: selectedOption.dataset.optionValueId ?? '',
//     }));

//     const isOnProductPage =
//       this.dataset.templateProductMatch === 'true' &&
//       !event.target.closest('product-card') &&
//       !event.target.closest('quick-add-dialog');

//     // Morph the entire main content for combined listings child products, because changing the product
//     // might also change other sections depending on recommendations, metafields, etc.
//     const currentUrl = this.dataset.productUrl?.split('?')[0];
//     const newUrl = selectedOption.dataset.connectedProductUrl;
//     const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
//     const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

//     const morphElementSelector = loadsNewProduct
//       ? 'main'
//       : isOnFeaturedProductSection
//       ? 'featured-product-information'
//       : undefined;

//     this.fetchUpdatedSection(
//       this.buildRequestUrl(selectedOption),
//       morphElementSelector,
//       selectedOption.dataset.optionValueId
//     );

//     const url = new URL(window.location.href);

//     const variantId = selectedOption.dataset.variantId || null;

//     if (isOnProductPage) {
//       if (variantId) {
//         url.searchParams.set('variant', variantId);
//       } else {
//         url.searchParams.delete('variant');
//       }
//     }

//     // Change the path if the option is connected to another product via combined listing.
//     if (loadsNewProduct) {
//       url.pathname = newUrl;
//     }

//     if (url.href !== window.location.href) {
//       yieldToMainThread().then(() => {
//         history.replaceState({}, '', url.toString());
//       });
//     }
//   }

//   /**
//    * @typedef {object} FieldsetMeasurements
//    * @property {HTMLFieldSetElement} fieldset
//    * @property {number | undefined} currentIndex
//    * @property {number | undefined} previousIndex
//    * @property {number | undefined} currentWidth
//    * @property {number | undefined} previousWidth
//    */

//   /**
//    * Gets measurements for a single fieldset (read phase).
//    * @param {number} fieldsetIndex
//    * @returns {FieldsetMeasurements | null}
//    */
//   #getFieldsetMeasurements(fieldsetIndex) {
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     const fieldset = fieldsets[fieldsetIndex];
//     const checkedIndices = this.#checkedIndices[fieldsetIndex];
//     const radios = this.#radios[fieldsetIndex];

//     if (!radios || !checkedIndices || !fieldset) return null;

//     const [currentIndex, previousIndex] = checkedIndices;

//     return {
//       fieldset,
//       currentIndex,
//       previousIndex,
//       currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
//       previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
//     };
//   }

//   /**
//    * Applies measurements to a fieldset (write phase).
//    * @param {FieldsetMeasurements} measurements
//    */
//   #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
//     if (currentWidth) {
//       fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
//     } else if (currentIndex !== undefined) {
//       fieldset.style.removeProperty('--pill-width-current');
//     }

//     if (previousWidth) {
//       fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
//     } else if (previousIndex !== undefined) {
//       fieldset.style.removeProperty('--pill-width-previous');
//     }
//   }

//   /**
//    * Updates the fieldset CSS.
//    * @param {number} fieldsetIndex - The fieldset index.
//    */
//   updateFieldsetCss(fieldsetIndex) {
//     if (Number.isNaN(fieldsetIndex)) return;

//     const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
//     if (measurements) {
//       this.#applyFieldsetMeasurements(measurements);
//     }
//   }

//   /**
//    * Updates the selected option.
//    * @param {string | Element} target - The target element.
//    */
//   updateSelectedOption(target) {
//     if (typeof target === 'string') {
//       const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

//       if (!targetElement) throw new Error('Target element not found');

//       target = targetElement;
//     }

//     if (target instanceof HTMLInputElement) {
//       const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
//       const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

//       if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
//         const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//         const fieldset = fieldsets[fieldsetIndex];
//         const checkedIndices = this.#checkedIndices[fieldsetIndex];
//         const radios = this.#radios[fieldsetIndex];

//         if (radios && checkedIndices && fieldset) {
//           // Clear previous checked states
//           const [currentIndex, previousIndex] = checkedIndices;

//           if (currentIndex !== undefined && radios[currentIndex]) {
//             radios[currentIndex].dataset.previousChecked = 'false';
//           }
//           if (previousIndex !== undefined && radios[previousIndex]) {
//             radios[previousIndex].dataset.previousChecked = 'false';
//           }

//           // Update checked indices array - keep only the last 2 selections
//           checkedIndices.unshift(inputIndex);
//           checkedIndices.length = Math.min(checkedIndices.length, 2);

//           // Update the new states
//           const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
//           const newPreviousIndex = checkedIndices[1]; // This might be undefined

//           // newCurrentIndex is guaranteed to exist since we just added it
//           if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
//             radios[newCurrentIndex].dataset.currentChecked = 'true';
//           }

//           if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
//             radios[newPreviousIndex].dataset.previousChecked = 'true';
//             radios[newPreviousIndex].dataset.currentChecked = 'false';
//           }

//           this.updateFieldsetCss(fieldsetIndex);
//         }
//       }
//       target.checked = true;
//     }

//     if (target instanceof HTMLSelectElement) {
//       const newValue = target.value;
//       const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

//       if (!newSelectedOption) throw new Error('Option not found');

//       for (const option of target.options) {
//         option.removeAttribute('selected');
//       }

//       newSelectedOption.setAttribute('selected', 'selected');
//     }
//   }

//   /**
//    * Builds the request URL.
//    * @param {HTMLElement} selectedOption - The selected option.
//    * @param {string | null} [source] - The source.
//    * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
//    * @returns {string} The request URL.
//    */
//   buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
//     // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
//     // We create a new URL and abort the previous fetch request if it's still pending.
//     let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
//     this.#pendingRequestUrl = productUrl;
//     const params = [];
//     const viewParamValue = getViewParameterValue();

//     // preserve view parameter, if it exists, for alternative product view testing
//     if (viewParamValue) params.push(`view=${viewParamValue}`);

//     if (this.selectedOptionsValues.length && !source) {
//       params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
//     } else if (source === 'product-card') {
//       if (this.selectedOptionsValues.length) {
//         params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
//       } else {
//         params.push(`option_values=${selectedOption.dataset.optionValueId}`);
//       }
//     }

//     // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
//     const SECTION_ID_MAP = {
//       'quick-add-component': 'section-rendering-product-card',
//       'swatches-variant-picker-component': 'section-rendering-product-card',
//       'featured-product-information': this.closest('featured-product-information')?.id,
//     };

//     const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
//       Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
//     );

//     if (closestSectionId) {
//       if (productUrl?.includes('?')) {
//         productUrl = productUrl.split('?')[0];
//       }
//       return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
//     }

//     return `${productUrl}?${params.join('&')}`;
//   }

//   /**
//    * Builds the fallback request URL with only the specified option value ID.
//    * @param {string} optionValueId
//    * @returns {string}
//    */
//   buildRequestUrlForOptionValue(optionValueId) {
//     let productUrl = this.#pendingRequestUrl || this.dataset.productUrl;
//     const params = [];
//     const viewParamValue = getViewParameterValue();

//     if (viewParamValue) params.push(`view=${viewParamValue}`);
//     params.push(`option_values=${optionValueId}`);

//     const SECTION_ID_MAP = {
//       'quick-add-component': 'section-rendering-product-card',
//       'swatches-variant-picker-component': 'section-rendering-product-card',
//       'featured-product-information': this.closest('featured-product-information')?.id,
//     };

//     const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
//       Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
//     );

//     if (closestSectionId) {
//       if (productUrl?.includes('?')) {
//         productUrl = productUrl.split('?')[0];
//       }
//       return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
//     }

//     return `${productUrl}?${params.join('&')}`;
//   }

//   /**
//    * Fetches the updated section.
//    * @param {string} requestUrl - The request URL.
//    * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
//    * @param {string} [primaryOptionValueId] - The option value ID selected by the user.
//    * @param {boolean} [isRetry] - Whether this fetch is a fallback retry.
//    */
//   fetchUpdatedSection(requestUrl, morphElementSelector, primaryOptionValueId, isRetry = false) {
//     // We use this to abort the previous fetch request if it's still pending.
//     this.#abortController?.abort();
//     this.#abortController = new AbortController();

//     fetch(requestUrl, { signal: this.#abortController.signal })
//       .then((response) => response.text())
//       .then((responseText) => {
//         this.#pendingRequestUrl = undefined;
//         const html = new DOMParser().parseFromString(responseText, 'text/html');
//         // Defer is only useful for the initial rendering of the page. Remove it here.
//         html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

//         const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
//         const parsedVariant = textContent ? JSON.parse(textContent) : null;

//         // If the combination returned null/invalid variant, retry immediately with just the clicked option value
//         if (!parsedVariant && !isRetry && primaryOptionValueId) {
//           const fallbackUrl = this.buildRequestUrlForOptionValue(primaryOptionValueId);
//           this.fetchUpdatedSection(fallbackUrl, morphElementSelector, primaryOptionValueId, true);
//           return;
//         }

//         if (!textContent) return;

//         let newProduct;

//         if (morphElementSelector === 'main') {
//           this.updateMain(html);
//         } else if (morphElementSelector) {
//           this.updateElement(html, morphElementSelector);
//         } else {
//           const { overflowList } = this.refs;
//           const wasSwatchesExpanded =
//             overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

//           newProduct = this.updateVariantPicker(html);

//           if (wasSwatchesExpanded) {
//             const overflowListAfterMorph = overflowList;
//             if (overflowListAfterMorph instanceof OverflowList) {
//               overflowListAfterMorph.showAll();
//             }
//           }
//         }

//         this.ensureDefaultOptionSelected();

//         // Update URL on product page if a valid variant was resolved
//         const isOnProductPage =
//           this.dataset.templateProductMatch === 'true' &&
//           !this.closest('product-card') &&
//           !this.closest('quick-add-dialog');

//         if (isOnProductPage && parsedVariant?.id) {
//           const url = new URL(window.location.href);
//           url.searchParams.set('variant', parsedVariant.id);
//           if (url.href !== window.location.href) {
//             yieldToMainThread().then(() => {
//               history.replaceState({}, '', url.toString());
//             });
//           }
//         }

//         // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
//         if (this.selectedOptionId) {
//           this.dispatchEvent(
//             new VariantUpdateEvent(parsedVariant, this.selectedOptionId, {
//               html,
//               productId: this.dataset.productId ?? '',
//               newProduct,
//             })
//           );
//         }
//       })
//       .catch((error) => {
//         if (error.name === 'AbortError') {
//           console.warn('Fetch aborted by user');
//         } else {
//           console.error(error);
//         }
//       });
//   }

//   /**
//    * @typedef {Object} NewProduct
//    * @property {string} id
//    * @property {string} url
//    */

//   /**
//    * Re-renders the variant picker.
//    * @param {Document | Element} newHtml - The new HTML.
//    * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
//    */
//   updateVariantPicker(newHtml) {
//     /** @type {NewProduct | undefined} */
//     let newProduct;

//     const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

//     if (!newVariantPickerSource) {
//       throw new Error('No new variant picker source found');
//     }

//     // For combined listings, the product might have changed, so update the related data attribute.
//     if (newVariantPickerSource instanceof HTMLElement) {
//       const newProductId = newVariantPickerSource.dataset.productId;
//       const newProductUrl = newVariantPickerSource.dataset.productUrl;

//       if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
//         newProduct = { id: newProductId, url: newProductUrl };
//       }

//       this.dataset.productId = newProductId;
//       this.dataset.productUrl = newProductUrl;
//     }

//     morph(this, newVariantPickerSource, {
//       ...MORPH_OPTIONS,
//       getNodeKey: (node) => {
//         if (!(node instanceof HTMLElement)) return undefined;
//         const key = node.dataset.key;
//         return key;
//       },
//     });

//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     this.#radios = [];
//     this.#checkedIndices = [];
//     fieldsets.forEach((fieldset) => {
//       const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
//       this.#radios.push(radios);

//       const initialCheckedIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
//       if (initialCheckedIndex !== -1) {
//         this.#checkedIndices.push([initialCheckedIndex]);
//       } else {
//         this.#checkedIndices.push([]);
//       }
//     });

//     this.ensureDefaultOptionSelected();
//     this.updateVariantPickerCss();

//     return newProduct;
//   }

//   updateVariantPickerCss() {
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

//     // Batch all reads first across all fieldsets to avoid layout thrashing
//     const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

//     // Batch all writes after all reads
//     for (const measurement of measurements) {
//       this.#applyFieldsetMeasurements(measurement);
//     }
//   }

//   /**
//    * Re-renders the desired element.
//    * @param {Document} newHtml - The new HTML.
//    * @param {string} elementSelector - The selector of the element to re-render.
//    */
//   updateElement(newHtml, elementSelector) {
//     const element = this.closest(elementSelector);
//     const newElement = newHtml.querySelector(elementSelector);

//     if (!element || !newElement) {
//       throw new Error(`No new element source found for ${elementSelector}`);
//     }

//     morph(element, newElement);
//   }

//   /**
//    * Re-renders the entire main content.
//    * @param {Document} newHtml - The new HTML.
//    */
//   updateMain(newHtml) {
//     const main = document.querySelector('main');
//     const newMain = newHtml.querySelector('main');

//     if (!main || !newMain) {
//       throw new Error('No new main source found');
//     }

//     morph(main, newMain);
//   }

//   /**
//    * Gets the selected option.
//    * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
//    */
//   get selectedOption() {
//     const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

//     if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
//       return undefined;
//     }

//     return selectedOption;
//   }

//   /**
//    * Gets the selected option ID.
//    * @returns {string | undefined} The selected option ID.
//    */
//   get selectedOptionId() {
//     const { selectedOption } = this;
//     if (!selectedOption) return undefined;
//     const { optionValueId } = selectedOption.dataset;

//     if (!optionValueId) {
//       throw new Error('No option value ID found');
//     }

//     return optionValueId;
//   }

//   /**
//    * Gets the selected options values.
//    * @returns {string[]} The selected options values.
//    */
//   get selectedOptionsValues() {
//     /** @type HTMLElement[] */
//     const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

//     return selectedOptions.map((option) => {
//       const { optionValueId } = option.dataset;

//       if (!optionValueId) throw new Error('No option value ID found');

//       return optionValueId;
//     });
//   }
// }

// if (!customElements.get('variant-picker')) {
//   customElements.define('variant-picker', VariantPicker);
// }
